import React, { useState } from "react";
import { FlightSearchState } from "./types";
import DatePicker from "./DatePicker";
import PassengerCabinSelector from "./PassengerCabinSelector";

interface SearchFieldsProps {
    searchState: FlightSearchState;
    onSearchStateChange: (state: Partial<FlightSearchState>) => void;
}

const SearchFields: React.FC<SearchFieldsProps> = ({
    searchState,
    onSearchStateChange,
}) => {
    const {
        tripType,
        departure,
        destination,
        departureDate,
        returnDate,
        passengers,
    } = searchState;

    const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
    const [isPassengerSelectorOpen, setIsPassengerSelectorOpen] =
        useState(false);

    const handleDatesChange = (
        newDepartureDate: string,
        newReturnDate: string
    ) => {
        onSearchStateChange({
            departureDate: newDepartureDate,
            returnDate: newReturnDate,
        });
    };

    return (
        <>
            {/* Desktop Layout - Also shown in landscape mobile */}
            <div className="hidden lg:flex landscape:flex gap-3 items-end pb-4 border-b border-gray-300 -mx-8 px-8">
                {/* Departure */}
                <div
                    className={tripType === "one-way" ? "flex-[1.2]" : "flex-1"}
                >
                    <label
                        className="block text-sm mb-1"
                        style={{ color: "#0047ba" }}
                    >
                        Departure
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            value={departure}
                            onChange={(e) =>
                                onSearchStateChange({
                                    departure: e.target.value,
                                })
                            }
                            className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                            placeholder="From"
                        />
                    </div>
                </div>

                {/* Swap Icon */}
                <div className="-ml-2 -mr-2">
                    <button
                        type="button"
                        onClick={() => {
                            const temp = departure;
                            onSearchStateChange({
                                departure: destination,
                                destination: temp,
                            });
                        }}
                        className="p-2 hover:bg-gray-100 rounded-full"
                    >
                        <svg
                            className="w-6 h-6"
                            style={{ color: "#0047ba" }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7h12m0 0l-4-4m4 4l-4 4m0 6H4m0 0l4 4m-4-4l4-4"
                            />
                        </svg>
                    </button>
                </div>

                {/* Destination */}
                <div
                    className={tripType === "one-way" ? "flex-[1.2]" : "flex-1"}
                >
                    <label
                        className="block text-sm mb-1"
                        style={{ color: "#0047ba" }}
                    >
                        Destination
                    </label>
                    <input
                        type="text"
                        value={destination}
                        onChange={(e) =>
                            onSearchStateChange({ destination: e.target.value })
                        }
                        className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                        placeholder="To"
                    />
                </div>

                {/* Departure Date */}
                <div
                    className={`${
                        tripType === "one-way" ? "flex-[1.2]" : "flex-1"
                    } border-l border-gray-300 pl-3`}
                >
                    <label
                        className="block text-sm mb-1"
                        style={{ color: "#0047ba" }}
                    >
                        Departure date
                    </label>
                    <div className="relative">
                        <input
                            type="text"
                            value={departureDate}
                            onChange={(e) =>
                                onSearchStateChange({
                                    departureDate: e.target.value,
                                })
                            }
                            className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                        />
                    </div>
                </div>

                {/* Return Date with Calendar Icon */}
                <div
                    className={
                        tripType !== "one-way" ? "flex-1" : "flex items-end"
                    }
                >
                    {tripType !== "one-way" && (
                        <label
                            className="block text-sm mb-1"
                            style={{ color: "#0047ba" }}
                        >
                            Return date
                        </label>
                    )}
                    <div className={tripType !== "one-way" ? "relative" : ""}>
                        {tripType !== "one-way" ? (
                            <>
                                <input
                                    type="text"
                                    value={returnDate}
                                    onChange={(e) =>
                                        onSearchStateChange({
                                            returnDate: e.target.value,
                                        })
                                    }
                                    className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent pr-6 p-0"
                                />
                                <button
                                    className="absolute right-2 top-1/2 -translate-y-1/2"
                                    type="button"
                                    onClick={() => setIsDatePickerOpen(true)}
                                >
                                    <svg
                                        className="w-5 h-5"
                                        style={{ color: "#0047ba" }}
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                        />
                                    </svg>
                                </button>
                            </>
                        ) : (
                            <button
                                className="p-2 hover:bg-gray-100 rounded-full"
                                type="button"
                                onClick={() => setIsDatePickerOpen(true)}
                            >
                                <svg
                                    className="w-5 h-5"
                                    style={{ color: "#0047ba" }}
                                    fill="none"
                                    stroke="currentColor"
                                    viewBox="0 0 24 24"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                                    />
                                </svg>
                            </button>
                        )}
                    </div>
                </div>

                {/* Pax and Cabin */}
                <div
                    className={`${
                        tripType === "one-way" ? "flex-[1.2]" : "flex-1"
                    } border-l border-gray-300 pl-3`}
                >
                    <label
                        className="block text-sm mb-1"
                        style={{ color: "#0047ba" }}
                    >
                        Pax and Cabin
                    </label>
                    <div className="relative">
                        <button
                            type="button"
                            onClick={() => setIsPassengerSelectorOpen(true)}
                            className="w-full text-left text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0 transition-colors"
                            onMouseEnter={(e) =>
                                (e.currentTarget.style.color = "#0047ba")
                            }
                            onMouseLeave={(e) =>
                                (e.currentTarget.style.color = "")
                            }
                        >
                            {passengers}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Layout - Single line fields */}
            <div className="lg:hidden landscape:hidden space-y-0">
                {/* Departure with swap icon */}
                <div className="relative">
                    <div className="border-b border-gray-300 -mx-2">
                        <div className="py-3 px-3 mr-10">
                            <label
                                className="block text-sm mb-1"
                                style={{ color: "#0047ba" }}
                            >
                                Departure
                            </label>
                            <input
                                type="text"
                                value={departure}
                                onChange={(e) =>
                                    onSearchStateChange({
                                        departure: e.target.value,
                                    })
                                }
                                className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                                placeholder="From"
                            />
                        </div>
                    </div>
                    {/* Swap icon positioned at the end of the border line */}
                    <button
                        type="button"
                        onClick={() => {
                            const temp = departure;
                            onSearchStateChange({
                                departure: destination,
                                destination: temp,
                            });
                        }}
                        className="absolute -right-2 bottom-0 transform translate-y-1/2 p-1 bg-white z-10"
                    >
                        <svg
                            className="w-5 h-5"
                            style={{ color: "#0047ba" }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16V4m0 0L3 8m4-4l4 4m6 0v12m0 0l4-4m-4 4l-4-4"
                            />
                        </svg>
                    </button>
                </div>

                {/* Destination */}
                <div className="border-b border-gray-300 -mx-2 pb-3">
                    <div className="py-3 px-3">
                        <label
                            className="block text-sm mb-1"
                            style={{ color: "#0047ba" }}
                        >
                            Destination
                        </label>
                        <input
                            type="text"
                            value={destination}
                            onChange={(e) =>
                                onSearchStateChange({
                                    destination: e.target.value,
                                })
                            }
                            className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                            placeholder="To"
                        />
                    </div>
                </div>

                {/* Departure Date */}
                <div className="relative pt-3">
                    <div className="border-b border-gray-300 -mx-2">
                        <div className="py-3 px-3 mr-10">
                            <label
                                className="block text-sm mb-1"
                                style={{ color: "#0047ba" }}
                            >
                                Departure date
                            </label>
                            <input
                                type="text"
                                value={departureDate}
                                onChange={(e) =>
                                    onSearchStateChange({
                                        departureDate: e.target.value,
                                    })
                                }
                                className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                                placeholder="Select date"
                            />
                        </div>
                    </div>
                    {/* Calendar icon positioned based on trip type */}
                    <button
                        type="button"
                        onClick={() => setIsDatePickerOpen(true)}
                        className={`absolute -right-2 p-1 bg-white z-10 ${
                            tripType === "one-way"
                                ? "top-1/2 transform -translate-y-1/2 mt-1.5"
                                : "bottom-0 transform translate-y-1/2"
                        }`}
                    >
                        <svg
                            className="w-5 h-5"
                            style={{ color: "#0047ba" }}
                            fill="none"
                            stroke="currentColor"
                            viewBox="0 0 24 24"
                        >
                            <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"
                            />
                        </svg>
                    </button>
                </div>

                {/* Return Date (if not one-way) */}
                {tripType !== "one-way" && (
                    <div className="border-b border-gray-300 -mx-2 pb-3">
                        <div className="py-3 px-3">
                            <label
                                className="block text-sm mb-1"
                                style={{ color: "#0047ba" }}
                            >
                                Return date
                            </label>
                            <input
                                type="text"
                                value={returnDate}
                                onChange={(e) =>
                                    onSearchStateChange({
                                        returnDate: e.target.value,
                                    })
                                }
                                className="w-full text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                                placeholder="Select date"
                            />
                        </div>
                    </div>
                )}

                {/* Pax and Cabin - No bottom border */}
                <div className="-mx-2 pt-3">
                    <div className="py-3 px-3">
                        <label
                            className="block text-sm mb-1"
                            style={{ color: "#0047ba" }}
                        >
                            Pax and Cabin
                        </label>
                        <button
                            type="button"
                            onClick={() => setIsPassengerSelectorOpen(true)}
                            className="w-full text-left text-base text-gray-900 border-0 outline-none focus:outline-none bg-transparent p-0"
                        >
                            {passengers}
                        </button>
                    </div>
                </div>
            </div>

            <DatePicker
                isOpen={isDatePickerOpen}
                onClose={() => setIsDatePickerOpen(false)}
                departureDate={departureDate}
                returnDate={returnDate}
                onDatesChange={handleDatesChange}
                tripType={tripType}
            />

            <PassengerCabinSelector
                isOpen={isPassengerSelectorOpen}
                onClose={() => setIsPassengerSelectorOpen(false)}
                initialPassengers={passengers}
                initialDetails={searchState.passengerDetails}
                onUpdate={(value, details) =>
                    onSearchStateChange({
                        passengers: value,
                        passengerDetails: details,
                    })
                }
            />
        </>
    );
};

export default SearchFields;
